import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import { Col, FormGroup, Input, Row } from "reactstrap";

import { Btn } from "../../../../../../AbstractElements";
import LabelTooltip from "../../../../../../CommonElements/LabelTooltip";
import MultiSelectDropdown from "../../../../../../CommonElements/MultiSelectDropdown";
import CheckboxButton from "../../../../../../CommonElements/SwitchButton";
import UploadedFileItem from "../../../../../../container/ThumbnailPreview";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../ReduxToolkit/Hooks";
import {
  getDomainList,
  getProficiencyList,
} from "../../../../../../ReduxToolkit/Reducers/MasterSlice";
import {
  Description,
  DescriptionValidationMessage,
  Domain,
  DurationInDays,
  DurationNumberValidationMessage,
  EgDays,
  Enter,
  JobRole,
  LearningPlanAutoAssignment,
  LearningPlanAutoAssignTooltipMessage,
  LearningPlanDescriptionTooltipMessage,
  LearningPlanDomainTooltipMessage,
  LearningPlanJobRoleTooltipMessage,
  LearningPlanLevelTooltipMessage,
  LearningPlanName,
  LearningPlanNameTooltipMessage,
  LearningPlanThumbnailTooltipMessage,
  Level,
  NameValidationMessage,
  OptInPreviousScore,
  Select,
  ThumbnailMaxSizeRequired,
  UploadThumbnail,
} from "../../../../../../utils/Constant";
import {
  handleKeyDownOnNumericWithoutDecimal,
  MAX_FILE_SIZE_IN_BYTES,
  showToast,
  validateValue,
} from "../../../../../../utils/helper/helper";

const LearningPlanBasicDetails = ({
  domainListData,
  jobRoleListData,
  basicInputFormValue,
  setBasicInputFormValue,
  errors,
  setErrors,
  learningPlanId,
}) => {
  const dispatch = useAppDispatch();
  const { proficiencyList } = useAppSelector(state => state.master);
  const [proficiencyListData, setProficiencyListData] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(getDomainList());
    dispatch(getProficiencyList());
  }, [dispatch]);

  useEffect(() => {
    const proficiencies = [];
    proficiencyList?.proficiency?.forEach(element => {
      proficiencies.push({
        label: element.level_name,
        value: element.id.toString(),
      });
    });
    setProficiencyListData(proficiencies);
  }, [proficiencyList]);

  const validateField = (
    name: string,
    value: string,
    isRequired = false,
    isNumeric = false,
    minValue?: number,
    maxValue?: number
  ): string | null => {
    const validationResponse = validateValue(
      name,
      value,
      isRequired,
      isNumeric,
      minValue,
      maxValue
    );

    return validationResponse.isValid ? null : getValidationMessage(name);
  };

  const getValidationMessage = (name: string): string => {
    switch (name) {
      case "learning_plan_name":
        return NameValidationMessage;
      case "duration":
        return DurationNumberValidationMessage;
      case "description":
        return DescriptionValidationMessage;
      default:
        return "";
    }
  };

  const validateInput = (name: string, value: string): string | null => {
    const fieldValidations: {
      [key: string]: [boolean, boolean, number?, number?];
    } = {
      learning_plan_name: [true, false],
      duration: [false, true, 1, 100],
      description: [true, false],
    };

    const validationParams = fieldValidations[name];
    if (validationParams) {
      return validateField(name, value, ...validationParams);
    }

    return null;
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    const error = validateInput(name, value);
    if (!error || value === "")
      setBasicInputFormValue(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleMultiInputChange = (name: string, value: string[]) => {
    const dataMap: Record<string, any[]> = {
      domain: domainListData || [],
      job_role: jobRoleListData || [],
    };

    const selectedData =
      dataMap[name]
        ?.filter(item => value.includes(item.name))
        .map(item => ({
          name: item.name,
          value: item.label,
          label: item.label,
        })) || [];

    setBasicInputFormValue(prev => ({ ...prev, [name]: selectedData }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_IN_BYTES) {
        showToast(ThumbnailMaxSizeRequired, "error");
        e.target.value = "";
        return;
      }

      setBasicInputFormValue(prev => ({
        ...prev,
        thumbnail_url: file,
      }));
    }
  };

  const handleRemoveImage = () => {
    setBasicInputFormValue(prev => ({
      ...prev,
      thumbnail_url: null,
      thumbnail_file: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleThumbnailButtonClick = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      <Row className="mt-2">
        <Col lg="6" md="6" sm="12" xs="12">
          <FormGroup>
            <LabelTooltip
              label={LearningPlanName}
              tooltipText={LearningPlanNameTooltipMessage}
              important
            />
            <Input
              onChange={handleInputChange}
              value={basicInputFormValue?.learning_plan_name}
              name="learning_plan_name"
              type="text"
              placeholder={`${Enter} ${LearningPlanName}`}
              maxLength={100}
              invalid={!!errors.learning_plan_name}
            />
            {errors.learning_plan_name && (
              <div className="invalid-feedback">
                {errors.learning_plan_name}
              </div>
            )}
          </FormGroup>
        </Col>
        <Col lg="6" md="6" sm="12" xs="12">
          <LabelTooltip
            label={Domain}
            tooltipText={LearningPlanDomainTooltipMessage}
            important
          />
          <MultiSelectDropdown
            onChange={values => handleMultiInputChange("domain", values)}
            options={domainListData}
            placeholder={`${Select} ${Domain}`}
            defaultSelected={basicInputFormValue?.domain}
          />
          <div className="text-danger">{errors.domain}</div>
        </Col>
      </Row>
      <Row>
        <Col lg="6" md="6" sm="12" xs="12">
          <FormGroup>
            <LabelTooltip
              label={JobRole}
              tooltipText={LearningPlanJobRoleTooltipMessage}
              important={basicInputFormValue?.auto_assign}
            />
            <MultiSelectDropdown
              onChange={values => handleMultiInputChange("job_role", values)}
              options={jobRoleListData}
              placeholder={`${Select} ${JobRole}`}
              defaultSelected={basicInputFormValue?.job_role}
              enableSelectDeselectAll={true}
            />
            <div className="text-danger">{errors.job_role}</div>
          </FormGroup>
        </Col>
        <Col lg="6" md="6" sm="12" xs="12">
          <FormGroup>
            <LabelTooltip
              important
              label={DurationInDays}
              tooltipText={`${Enter} ${DurationInDays}`}
            />
            <Input
              type="number"
              name="duration"
              inputMode="numeric"
              value={basicInputFormValue?.duration}
              onChange={handleInputChange}
              placeholder={EgDays}
              onKeyDown={handleKeyDownOnNumericWithoutDecimal}
              min={0}
              max={100}
              invalid={!!errors.duration}
            />
            {errors.duration && (
              <div className="invalid-feedback">{errors.duration}</div>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col lg="6" md="6" sm="12" xs="12">
          <FormGroup>
            <LabelTooltip
              label={Level}
              tooltipText={LearningPlanLevelTooltipMessage}
              important={false}
            />
            <Input
              onChange={handleInputChange}
              value={basicInputFormValue?.level || ""}
              name="level"
              type="select"
              invalid={!!errors.level}
            >
              <option value="" disabled>
                {Select} {Level}
              </option>
              {proficiencyListData?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
            <div className="text-danger">{errors.level}</div>
          </FormGroup>
        </Col>
        <Col lg="6" md="6" sm="12" xs="12">
          <FormGroup>
            <LabelTooltip
              label={UploadThumbnail}
              tooltipText={LearningPlanThumbnailTooltipMessage}
              important
            />
            <Row>
              <Col lg="4" md="4" sm="12" xs="12">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
                <Btn
                  outline
                  className="alert-light-primary"
                  color="primary"
                  onClick={handleThumbnailButtonClick}
                >
                  {UploadThumbnail}
                </Btn>
              </Col>
              <Col lg="8" md="8" sm="12" xs="12">
                {basicInputFormValue?.thumbnail_url && (
                  <UploadedFileItem
                    fileName={basicInputFormValue?.thumbnail_url}
                    thumbnailUrl={basicInputFormValue?.thumbnail_url}
                    onDelete={handleRemoveImage}
                  />
                )}
              </Col>
            </Row>
            <div className="text-danger">{errors.thumbnail_url}</div>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col lg="12" md="12" sm="12" xs="12">
          <FormGroup>
            <LabelTooltip
              label={Description}
              tooltipText={LearningPlanDescriptionTooltipMessage}
              important
            />
            <Input
              rows={4}
              onChange={handleInputChange}
              value={basicInputFormValue?.description}
              name="description"
              type="textarea"
              placeholder={`${Enter} ${Description}`}
              maxLength={500}
              invalid={!!errors.description}
            />
            {errors.description && (
              <div className="invalid-feedback">{errors.description}</div>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col lg="6" md="6" sm="12" xs="12">
          <FormGroup className="d-flex gap-2">
            <p className="fw-bold">
              <LabelTooltip
                label={LearningPlanAutoAssignment}
                tooltipText={LearningPlanAutoAssignTooltipMessage}
                important={false}
              />
            </p>
            <div className="mt-1">
              <CheckboxButton
                onChange={e => {
                  setBasicInputFormValue(prev => ({
                    ...prev,
                    ["auto_assign"]: e.target.checked,
                  }));
                }}
                checked={basicInputFormValue?.auto_assign}
              />
            </div>
          </FormGroup>
        </Col>
        <Col lg="6" md="6" sm="12" xs="12">
          <FormGroup className="d-flex gap-2">
            <p className="fw-bold">
              <LabelTooltip
                label={OptInPreviousScore}
                tooltipText={OptInPreviousScore}
                important={false}
              />
            </p>
            <div className="mt-1">
              <CheckboxButton
                onChange={e => {
                  setBasicInputFormValue(prev => ({
                    ...prev,
                    ["opt_in"]: e.target.checked,
                  }));
                }}
                disabled={
                  learningPlanId &&
                  learningPlanId !== null &&
                  learningPlanId !== ""
                }
                checked={basicInputFormValue?.opt_in}
              />
            </div>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

export default LearningPlanBasicDetails;

LearningPlanBasicDetails.propTypes = {
  domainListData: PropTypes.array,
  jobRoleListData: PropTypes.array,
  basicInputFormValue: PropTypes.object.isRequired,
  setBasicInputFormValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  learningPlanId: PropTypes.any,
};
